import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { sectors, syncState } from "@/plugins/LocalForagePlugin";
import { sectorsSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { ContentfulSectors, SectorContentfulInfo } from "@/types/api/content-models";
import type { InvestorsIntelligenceSector } from "@/types/entities";
import type { Sector, GetSectorsResponse } from "@/types/api/market-data";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshSectorsFromServer( authToken: string | null ): Promise< InvestorsIntelligenceSector[] > {
    let allSectors: InvestorsIntelligenceSector[] = [];

    const t0: number = performance.now();

    const getTmiSectorsResponse = await goFetch<GetSectorsResponse, PersistedQueryPayload>
        ({
            endpoint: 'marketdata/graphql',
            payload: {
                id: 'dffa6c06092b1863cedee02a210dfc01'
            },
            authToken: authToken
        } );

    const tmiSectors = new Map< string, InvestorsIntelligenceSector >();

    if ( null != getTmiSectorsResponse?.data?.sectors?.items )
    {
        getTmiSectorsResponse.data.sectors.items.forEach( ( node: Sector ) =>
        {
            tmiSectors.set( node.symbol.toLowerCase(),
            {
                id: node.id,
                iiChartId: node.indexChartId,
                name: node.name,
                numStocksInSector: node.stockCount,
                numEtfsInSector: node.etfCount,
                symbol: node.symbol,
                // Data that comes from Contentful
                slug: '',
                contentfulId: '',
            } );
        } );
    
        let page: number = 0;
        const pageSize: number = 50;

        while (true) {
            const response = await goFetch<ContentfulSectors, PersistedQueryPayload>(
            {
                endpoint: 'content/graphql',
                payload: {
                    id: 'e82f7b992a593849eea4503aa3f594b9',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                },
                authToken: authToken
            } );

            // If no response or no data, stop pagination
            const currentPageSectors = response?.data?.sectorCatalog?.sectors;
            if (!currentPageSectors || currentPageSectors.length === 0) {
                break;
            }

            // Process the sectors from this page
            currentPageSectors.forEach( ( info: SectorContentfulInfo ) => {
                const tmiSector: InvestorsIntelligenceSector | undefined = tmiSectors.get( info.short_name.toLowerCase() );

                if ( tmiSector ) {
                    tmiSector.slug = info.slug.toLowerCase();
                    tmiSector.contentfulId = info.id;
                }
            } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ currentPageSectors.length } sector items (page ${page}) took ${ t1 - t0 } ms` );

            // Stop if this page had fewer items than pageSize (last page)
            if (currentPageSectors.length < pageSize) {
                break;
            }

            page++;
        }

        allSectors = Array.from( tmiSectors, ( [ _, value ] ) => value );
    }

    return allSectors;
}

export async function loadSectorMaps( sectorByContentfulIdMap: Map< string, InvestorsIntelligenceSector >,
                                      sectorByTmiIdMap: Map< number, InvestorsIntelligenceSector >,
                                      authToken: string | null ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        // Verify that IDB has been initialized
        if ( typeof sectors === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( sectorsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {

            const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
            await syncState.setItem( sectorsSyncKey, nextSyncThresholdInMillis );

            const sectorsReceived: InvestorsIntelligenceSector[] = await refreshSectorsFromServer( authToken );

            if( sectorsReceived.length > 0 )
            {
                updated = true;

                // Store the items recently retrieved
                await sectors.ready();
                for ( const sector of sectorsReceived ) {
                    await sectors.setItem( sector.contentfulId, cloneDeep( sector ) );
                }

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ sectorsReceived.length } sectors from server in ${ t1 - t0 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( sectorsSyncKey, nextSyncThresholdInMillis );
            }
        }

        // Make sure maps is empty
        sectorByTmiIdMap.clear();
        sectorByContentfulIdMap.clear();
        await sectors.ready();
        // Iterate over all elements within IDB
        await sectors.iterate( ( sector: InvestorsIntelligenceSector ) => {
            sectorByContentfulIdMap.set( sector.contentfulId, sector );
            sectorByTmiIdMap.set( sector.id, sector );
            return;
        } );

    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
